import { useMemo } from "react";
import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  bulkAssignFamilies,
  createFamily,
  deleteFamily,
  getFamilies,
  getFamily,
  getFamilyAum,
  getFamilyMonthlySip,
  getFamilySchemeWiseHoldings,
  getMyFamilyAum,
  getMyFamilyContext,
  getMyFamilyHoldings,
  getMyFamilyMonthlySip,
  getMyFamilyRoster,
  getMyFamilySips,
  getMyFamilyStps,
  getMyFamilySwps,
  markMyFamilyFeatureViewed,
  getFamilyRecentActivity,
  getFamilyGoalsSummary,
  getMyFamilyGoalsSummary,
  updateFamily,
} from "../../api/clients";
import {
  BulkAssignFamiliesPayload,
  CreateFamilyPayload,
  FamiliesFilter,
  FamilySource,
  UpdateFamilyPayload,
} from "../../types/client";

export const familyKeys = {
  all: ["families"] as const,
  detail: (id: number) => ["families", id] as const,
  aum: (id: number, source: FamilySource) =>
    ["families", id, "aum", source] as const,
  monthlySip: (id: number) => ["families", id, "monthly-sip"] as const,
  schemeWiseHoldings: (id: number, source: FamilySource) =>
    ["families", id, "scheme-wise-holdings", source] as const,
  recentActivity: (id: number, days: number) =>
    ["families", id, "recent-activity", days] as const,
  goalsSummary: (id: number) => ["families", id, "goals-summary"] as const,
  myAum: (source: FamilySource) => ["my-family", "aum", source] as const,
  myMonthlySip: () => ["my-family", "monthly-sip"] as const,
  myRoster: (source: FamilySource) => ["my-family", "roster", source] as const,
  myGoalsSummary: () => ["my-family", "goals-summary"] as const,
  myContext: () => ["my-family", "context"] as const,
  myHoldings: (search?: string) =>
    ["my-family", "holdings", { search }] as const,
  mySips: (pageSize: number, search?: string) =>
    ["my-family", "sips", { pageSize, search }] as const,
  myStps: (pageSize: number, search?: string) =>
    ["my-family", "stps", { pageSize, search }] as const,
  mySwps: (pageSize: number, search?: string) =>
    ["my-family", "swps", { pageSize, search }] as const,
  infiniteAll: () => ["infinite-families"] as const,
  infiniteList: (search: string, filters?: FamiliesFilter) =>
    ["infinite-families", { search, ...filters }] as const,
};

export const familiesQueryOptions = (
  {
    page = 1,
    pageSize = 20,
    search = "",
    filters,
    sort,
  }: {
    page?: number;
    pageSize?: number;
    search?: string;
    filters?: FamiliesFilter;
    sort?: Array<{ id: string; desc: boolean }>;
  },
  options?: { enabled?: boolean }
) => {
  return queryOptions({
    queryKey: ["families", { page, pageSize, search, sort, ...filters }],
    queryFn: () => getFamilies({ page, pageSize, search, filters, sort }),
    placeholderData: (prev) => prev,
    enabled: options?.enabled ?? true,
  });
};

export const useFamilies = (
  variables: {
    page?: number;
    pageSize?: number;
    search?: string;
    filters?: FamiliesFilter;
    sort?: Array<{ id: string; desc: boolean }>;
  },
  options?: { enabled?: boolean }
) => useQuery(familiesQueryOptions(variables, options));

export const useFamiliesInfiniteQuery = (
  search: string = "",
  filters?: FamiliesFilter,
  options?: { enabled?: boolean }
) => {
  const response = useInfiniteQuery({
    queryKey: familyKeys.infiniteList(search, filters),
    queryFn: (context) =>
      getFamilies({
        page: context.pageParam,
        pageSize: 50,
        search,
        filters,
      }),
    getNextPageParam: (lastPage) => {
      return lastPage.next
        ? Number(lastPage.next.match(/page=(\d+)/)?.[1])
        : undefined;
    },
    initialPageParam: 1,
    select: (queryResponse) => queryResponse.pages,
    placeholderData: (prev) => prev,
    enabled: options?.enabled ?? true,
  });

  const families = useMemo(
    () => response.data?.flatMap((d) => d.results),
    [response.data]
  );

  return {
    ...response,
    families,
  };
};

export const familyDetailsQueryOptions = (
  id: number,
  options?: { enabled?: boolean }
) => {
  return queryOptions({
    queryKey: familyKeys.detail(id),
    queryFn: () => getFamily(id),
    enabled: options?.enabled ?? !!id,
  });
};

export function useFamily(id: number, enabled = true) {
  return useQuery(familyDetailsQueryOptions(id, { enabled }));
}

export const familyAumQueryOptions = (
  id: number,
  source: FamilySource = "OVERALL",
  options?: { enabled?: boolean }
) => {
  return queryOptions({
    queryKey: familyKeys.aum(id, source),
    queryFn: () => getFamilyAum(id, source),
    placeholderData: (prev) => prev,
    enabled: options?.enabled ?? !!id,
  });
};

export function useFamilyAum(
  id: number,
  source: FamilySource = "OVERALL",
  enabled = true
) {
  return useQuery(familyAumQueryOptions(id, source, { enabled }));
}

export const familyMonthlySipQueryOptions = (
  id: number,
  options?: { enabled?: boolean }
) => {
  return queryOptions({
    queryKey: familyKeys.monthlySip(id),
    queryFn: () => getFamilyMonthlySip(id),
    placeholderData: (prev) => prev,
    enabled: options?.enabled ?? !!id,
  });
};

export function useFamilyMonthlySip(id: number, enabled = true) {
  return useQuery(familyMonthlySipQueryOptions(id, { enabled }));
}

export const familySchemeWiseHoldingsQueryOptions = (
  id: number,
  source: FamilySource = "OVERALL",
  options?: { enabled?: boolean }
) => {
  return queryOptions({
    queryKey: familyKeys.schemeWiseHoldings(id, source),
    queryFn: () => getFamilySchemeWiseHoldings(id, source),
    placeholderData: (prev) => prev,
    enabled: options?.enabled ?? !!id,
  });
};

export function useFamilySchemeWiseHoldings(
  id: number,
  source: FamilySource = "OVERALL",
  enabled = true
) {
  return useQuery(
    familySchemeWiseHoldingsQueryOptions(id, source, { enabled })
  );
}

// Investor-only: AUM/monthly-SIP for the requesting investor's own family.
export const myFamilyAumQueryOptions = (
  source: FamilySource = "OVERALL",
  options?: { enabled?: boolean }
) => {
  return queryOptions({
    queryKey: familyKeys.myAum(source),
    queryFn: () => getMyFamilyAum(source),
    placeholderData: (prev) => prev,
    enabled: options?.enabled ?? true,
  });
};

export function useMyFamilyAum(
  source: FamilySource = "OVERALL",
  enabled = true
) {
  return useQuery(myFamilyAumQueryOptions(source, { enabled }));
}

export const myFamilyMonthlySipQueryOptions = (options?: {
  enabled?: boolean;
}) => {
  return queryOptions({
    queryKey: familyKeys.myMonthlySip(),
    queryFn: () => getMyFamilyMonthlySip(),
    placeholderData: (prev) => prev,
    enabled: options?.enabled ?? true,
  });
};

export function useMyFamilyMonthlySip(enabled = true) {
  return useQuery(myFamilyMonthlySipQueryOptions({ enabled }));
}

// Investor-only: roster for the requesting investor's own family.
export const myFamilyRosterQueryOptions = (
  source: FamilySource = "OVERALL",
  options?: { enabled?: boolean }
) => {
  return queryOptions({
    queryKey: familyKeys.myRoster(source),
    queryFn: () => getMyFamilyRoster(source),
    placeholderData: (prev) => prev,
    enabled: options?.enabled ?? true,
  });
};

export function useMyFamilyRoster(
  source: FamilySource = "OVERALL",
  enabled = true
) {
  return useQuery(myFamilyRosterQueryOptions(source, { enabled }));
}

// Investor-only bootstrap: which family state the caller is in, plus the
// ready-to-render account-switcher payload.
export const myFamilyContextQueryOptions = (options?: {
  enabled?: boolean;
}) => {
  return queryOptions({
    queryKey: familyKeys.myContext(),
    queryFn: () => getMyFamilyContext(),
    placeholderData: (prev) => prev,
    enabled: options?.enabled ?? true,
  });
};

export function useMyFamilyContext(enabled = true) {
  return useQuery(myFamilyContextQueryOptions({ enabled }));
}

// Investor-only: marks the family feature as viewed for the requesting
// investor.
export function useMarkMyFamilyFeatureViewed() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => markMyFamilyFeatureViewed(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: familyKeys.myContext() });
    },
  });
}

export const familyRecentActivityQueryOptions = (
  id: number,
  days = 7,
  options?: { enabled?: boolean }
) =>
  infiniteQueryOptions({
    queryKey: familyKeys.recentActivity(id, days),
    queryFn: ({ pageParam }) =>
      getFamilyRecentActivity(id, { days, page: pageParam }),
    getNextPageParam: (lastPage, pages) =>
      lastPage.next ? pages.length + 1 : undefined,
    initialPageParam: 1,
    enabled: options?.enabled ?? !!id,
  });

export function useFamilyRecentActivity(id: number, days = 7, enabled = true) {
  return useInfiniteQuery(
    familyRecentActivityQueryOptions(id, days, { enabled })
  );
}

export const familyGoalsSummaryQueryOptions = (
  id: number,
  options?: { enabled?: boolean }
) => {
  return queryOptions({
    queryKey: familyKeys.goalsSummary(id),
    queryFn: () => getFamilyGoalsSummary(id),
    placeholderData: (prev) => prev,
    enabled: options?.enabled ?? !!id,
  });
};

export function useFamilyGoalsSummary(id: number, enabled = true) {
  return useQuery(familyGoalsSummaryQueryOptions(id, { enabled }));
}

// Investor-only: goals summary for the requesting investor's own family.
export const myFamilyGoalsSummaryQueryOptions = (options?: {
  enabled?: boolean;
}) => {
  return queryOptions({
    queryKey: familyKeys.myGoalsSummary(),
    queryFn: () => getMyFamilyGoalsSummary(),
    placeholderData: (prev) => prev,
    enabled: options?.enabled ?? true,
  });
};

export function useMyFamilyGoalsSummary(enabled = true) {
  return useQuery(myFamilyGoalsSummaryQueryOptions({ enabled }));
}

// Investor-only: scheme-folio-wise holdings across the requesting investor's
// family.
export const myFamilyHoldingsQueryOptions = (
  search?: string,
  options?: { enabled?: boolean }
) => {
  return queryOptions({
    queryKey: familyKeys.myHoldings(search),
    queryFn: () => getMyFamilyHoldings(search),
    placeholderData: (prev) => prev,
    enabled: options?.enabled ?? true,
  });
};

export function useMyFamilyHoldings(search?: string, enabled = true) {
  return useQuery(myFamilyHoldingsQueryOptions(search, { enabled }));
}

type MyFamilyPlansVariables = { pageSize: number; search?: string };

// Mirrors the per-account plan lists: another page exists while the last one
// came back full.
const getNextPlansPage = <T>(
  lastPage: { results?: T[] },
  pages: unknown[],
  pageSize: number
) => (lastPage.results?.length === pageSize ? pages.length + 1 : undefined);

// Investor-only: SIP/STP/SWP plans across the requesting investor's family.
export function useInfiniteMyFamilySips(
  { pageSize, search }: MyFamilyPlansVariables,
  options?: { enabled?: boolean }
) {
  return useInfiniteQuery({
    queryKey: familyKeys.mySips(pageSize, search),
    queryFn: ({ pageParam }) =>
      getMyFamilySips({ page: pageParam, pageSize, search }),
    placeholderData: (prev) => prev,
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) =>
      getNextPlansPage(lastPage, pages, pageSize),
    enabled: options?.enabled ?? true,
  });
}

export function useInfiniteMyFamilyStps(
  { pageSize, search }: MyFamilyPlansVariables,
  options?: { enabled?: boolean }
) {
  return useInfiniteQuery({
    queryKey: familyKeys.myStps(pageSize, search),
    queryFn: ({ pageParam }) =>
      getMyFamilyStps({ page: pageParam, pageSize, search }),
    placeholderData: (prev) => prev,
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) =>
      getNextPlansPage(lastPage, pages, pageSize),
    enabled: options?.enabled ?? true,
  });
}

export function useInfiniteMyFamilySwps(
  { pageSize, search }: MyFamilyPlansVariables,
  options?: { enabled?: boolean }
) {
  return useInfiniteQuery({
    queryKey: familyKeys.mySwps(pageSize, search),
    queryFn: ({ pageParam }) =>
      getMyFamilySwps({ page: pageParam, pageSize, search }),
    placeholderData: (prev) => prev,
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) =>
      getNextPlansPage(lastPage, pages, pageSize),
    enabled: options?.enabled ?? true,
  });
}

export function useCreateFamily() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateFamilyPayload) => createFamily(payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: familyKeys.all });
      queryClient.invalidateQueries({ queryKey: familyKeys.infiniteAll() });
      queryClient.setQueryData(familyKeys.detail(data.id), data);
    },
  });
}

export function useUpdateFamily(id: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateFamilyPayload) => updateFamily(id, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: familyKeys.all });
      queryClient.setQueryData(familyKeys.detail(data.id), data);
    },
  });
}

export function useDeleteFamily(id: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => deleteFamily(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: familyKeys.all });
      queryClient.removeQueries({ queryKey: familyKeys.detail(id) });
    },
  });
}

export function useBulkAssignFamilies() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: BulkAssignFamiliesPayload) =>
      bulkAssignFamilies(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: familyKeys.all });
      queryClient.invalidateQueries({ queryKey: familyKeys.infiniteAll() });
    },
  });
}
